import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { useAuth } from '@/context/AuthContext'
import { useSarahUI } from '@/context/SarahUIContext'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { SarahAvatar } from '@/components/sarah/SarahAvatar'
import { domainEvents } from '@/services/events/domainEvents'
import type { Story, StoryNode, StoryProgress } from '@/domain/story'

// P4 Interactive Story player (Document 11 §P4). This component contains
// ZERO story-specific logic — it only ever reads `story.nodes[currentNodeId]`
// and renders whatever is there (text, an optional Sarah aside, choices or a
// single "Continue"). A second story is a new entry in seed/stories.ts, not
// a change here — the engine is the data model + this generic renderer.
export function StoryPlayerPage() {
  const { id } = useParams<{ id: string }>()
  const { stories, storyProgress, xp, badges } = useServices()
  const { currentUser } = useAuth()
  const { openSarah } = useSarahUI()
  const navigate = useNavigate()

  const [story, setStory] = useState<Story | null>(null)
  const [progress, setProgress] = useState<StoryProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [xpJustEarned, setXpJustEarned] = useState(0)
  const [newBadgeKeys, setNewBadgeKeys] = useState<string[]>([])
  const [resolving, setResolving] = useState(false)

  useEffect(() => {
    if (!id || !currentUser) return
    let cancelled = false

    async function load() {
      const s = await stories.getById(id!)
      if (!s) {
        setLoading(false)
        return
      }
      let p = await storyProgress.getProgress(currentUser!.id, id!)
      if (!p) {
        p = {
          userId: currentUser!.id,
          orgId: currentUser!.orgId,
          storyId: id!,
          currentNodeId: s.startNodeId,
          choiceLog: [],
          completed: false,
          outcome: null,
          startedAt: new Date().toISOString(),
          completedAt: null,
        }
        await storyProgress.saveProgress(p)
      }
      if (cancelled) return
      setStory(s)
      setProgress(p)
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [id, currentUser, stories, storyProgress])

  if (loading) return <LoadingSpinner label="Loading story…" />
  if (!story || !progress || !currentUser) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Story not found</h1>
        <Link to="/missions" className="cyber-btn-secondary mt-6 inline-flex">
          ← Back to Missions
        </Link>
      </div>
    )
  }

  const node: StoryNode | undefined = story.nodes[progress.currentNodeId]
  if (!node) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <p className="text-white/60">This story hit an unknown node — please replay.</p>
      </div>
    )
  }

  async function advance(nextNodeId: string, choiceId?: string) {
    if (!story || !progress || !currentUser || resolving) return
    setResolving(true)
    const nextNode = story.nodes[nextNodeId]
    const updated: StoryProgress = {
      ...progress,
      currentNodeId: nextNodeId,
      choiceLog: choiceId ? [...progress.choiceLog, { nodeId: progress.currentNodeId, choiceId }] : progress.choiceLog,
      completed: !!nextNode?.isEnding,
      outcome: nextNode?.isEnding ? nextNode.outcome ?? null : progress.outcome,
      completedAt: nextNode?.isEnding ? new Date().toISOString() : progress.completedAt,
    }
    await storyProgress.saveProgress(updated)
    setProgress(updated)

    if (nextNode?.isEnding) {
      const [xpBefore, badgesBefore] = await Promise.all([
        xp.getTotalForUser(currentUser.id),
        badges.listAwardsForUser(currentUser.id),
      ])
      await domainEvents.emit('story.completed', {
        userId: currentUser.id,
        orgId: currentUser.orgId,
        storyId: story.id,
        outcome: nextNode.outcome ?? 'risky',
      })
      const [xpAfter, badgesAfter] = await Promise.all([
        xp.getTotalForUser(currentUser.id),
        badges.listAwardsForUser(currentUser.id),
      ])
      setXpJustEarned(xpAfter - xpBefore)
      const beforeKeys = new Set(badgesBefore.map((b) => b.badgeKey))
      setNewBadgeKeys(badgesAfter.filter((b) => !beforeKeys.has(b.badgeKey)).map((b) => b.badgeKey))
    }
    setResolving(false)
  }

  async function handleReplay() {
    if (!story || !currentUser) return
    await storyProgress.resetProgress(currentUser.id, story.id)
    setXpJustEarned(0)
    setNewBadgeKeys([])
    const fresh: StoryProgress = {
      userId: currentUser.id,
      orgId: currentUser.orgId,
      storyId: story.id,
      currentNodeId: story.startNodeId,
      choiceLog: [],
      completed: false,
      outcome: null,
      startedAt: new Date().toISOString(),
      completedAt: null,
    }
    await storyProgress.saveProgress(fresh)
    setProgress(fresh)
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/missions" className="text-sm text-white/50 hover:text-white">
        ← Missions
      </Link>

      <div className="mt-4 flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-white">{story.title}</h1>
        {node.isEnding && (
          <span
            className={`cyber-pill ${
              node.outcome === 'good'
                ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30'
                : 'bg-coral-500/15 text-coral-500 ring-1 ring-coral-500/30'
            }`}
          >
            {node.outcome === 'good' ? 'Safe outcome' : 'Risky outcome'}
          </span>
        )}
      </div>

      <div className="mt-6 cyber-panel p-6">
        {node.speaker && <p className="text-xs font-medium uppercase tracking-wide text-teal-300">{node.speaker}</p>}
        <p className="mt-2 whitespace-pre-line text-white/90">{node.text}</p>
      </div>

      {node.sarahNote && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl bg-white/5 p-4">
          <SarahAvatar size={28} />
          <p className="text-sm text-white/70">{node.sarahNote}</p>
        </div>
      )}

      {node.isEnding ? (
        <div className="mt-8 space-y-4">
          <div className="cyber-panel p-6 text-center">
            {xpJustEarned > 0 && (
              <p className="cyber-pill mx-auto w-fit text-amber-300">+{xpJustEarned} Cyber Points earned</p>
            )}
            {newBadgeKeys.length > 0 && (
              <p className="mt-3 text-sm text-white/70">New achievement unlocked: {newBadgeKeys.join(', ')}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={handleReplay} className="cyber-btn-primary">
              Replay this story
            </button>
            <button
              onClick={() => openSarah({ episodeId: story.relatedEpisodeId, initialPrompt: `Explain ${story.topic}` })}
              className="cyber-btn-secondary"
            >
              Talk to Sarah about this
            </button>
            <button onClick={() => navigate('/missions')} className="cyber-btn-secondary">
              Back to Missions
            </button>
          </div>
        </div>
      ) : node.choices && node.choices.length > 0 ? (
        <div className="mt-6 space-y-3">
          {node.choices.map((choice) => (
            <button
              key={choice.id}
              disabled={resolving}
              onClick={() => advance(choice.nextNodeId, choice.id)}
              className="block w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm text-white/85 transition hover:border-teal-400/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {choice.text}
            </button>
          ))}
        </div>
      ) : node.nextNodeId ? (
        <div className="mt-6">
          <button disabled={resolving} onClick={() => advance(node.nextNodeId!)} className="cyber-btn-primary">
            Continue
          </button>
        </div>
      ) : null}
    </div>
  )
}
